/**
 * The clinic HRT / TRT screen's data: the shapes `GET /hrt/patients/:id` returns (engine v2.1, bands only), the
 * queries every HRT tab shares, and the small display helpers they have in common.
 *
 * One query key per patient, `['hrt', patientId]`: the header, the five HRT tabs and the dashboard row read the same
 * cached view, and every write (a lab, a confirmation, a pathway choice, a resolved alert) invalidates it, so the
 * engine's new evaluation reaches every tab at once.
 *
 * Clinicians see bands and labels only, never a percent or a chance of success: the API never sends the engine's
 * points, and nothing here derives one. Every clinical word shown comes from the API (which takes it from the
 * spec JSON) or, where a short display name is needed, from the developer spec's own tables, cited beside it.
 */

import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from './api';

/* ------------------------------------------------------------------ *
 * Shapes (apps/api/src/services/hrt.service.ts patientView)
 * ------------------------------------------------------------------ */

export type HrtTrack = 'male_trt' | 'female_hrt' | 'HRT-EXCEPTION' | 'UNKNOWN';
export type HrtBand = 'HIGH' | 'MODERATE' | 'LOW' | 'SUPPRESS' | 'LOCKED';
export type AlertLevel = 'RED' | 'ORANGE' | 'YELLOW';

export interface HrtFinding {
  id: string;
  /** The rule's name ("New possible clot symptoms"), shown in place of the bare id. */
  name: string;
  /** Clinician words: no internal number, band code or spec citation. */
  text: string;
  /** The spec citations taken out of the text, for the hint ("v1.2 tab 04", "decision 5"). */
  cite: string | null;
  unlock: string | null;
  /** What unlocks it, in words, and the confirmation that records it. */
  unlockText: string | null;
  unlockKey: string | null;
  referral: string | null;
  referralText: string | null;
  source: string | null;
  level: number | null;
}

export interface HrtEvidence {
  id: string;
  tier: string;
  citation: string;
  doi: string | null;
  url: string | null;
  status: string | null;
}

export interface HrtWhy {
  id?: string | null;
  why: string;
  evidence: string;
  before: string;
  sources: string[];
}

export interface HrtPathway {
  id: string;
  name: string;
  sex: 'male' | 'female';
  group: string;
  controlled: boolean;
  band: HrtBand;
  label: string;
  reviewRequired: boolean;
  section: 'consider' | 'not_relevant' | 'locked';
  rank: number;
  dominantPhenotype: { id: string; name: string | null } | null;
  summary: string | null;
  locks: HrtFinding[];
  caps: Array<{ id: string; name: string; text: string; cite: string | null }>;
  review: HrtFinding[];
  caution: HrtFinding[];
  flags: string[];
  missing: string[];
  requiresReasonOnSelect: string | null;
  why: HrtWhy | null;
  evidence: HrtEvidence[];
  regulatory: Array<{ id: string; text: string }>;
  monitoring: string[];
  medications: string[];
  nonHormonalOptions: { why: HrtWhy; evidence: HrtEvidence[] } | null;
}

export interface HrtPhenotype {
  id: string;
  name: string;
  active: boolean;
  /** The patient's answers point to it (a primary symptom Mild or worse, or the mixed and fertility rules). */
  pointsTo: boolean;
  leadsOn: string[];
  items: Array<{
    id: string;
    name: string;
    role: 'primary' | 'supporting' | 'cluster';
    refs: string[];
    severity: number | null;
    severityLabel: 'None' | 'Mild' | 'Moderate' | 'Severe' | 'Very severe' | null;
  }>;
}

export interface HrtProduct {
  medicationId: string;
  formularyItemId: string | null;
  name: string;
  doseLibraryName: string;
  role: 'primary' | 'adjunct';
  default: boolean;
  /** Ticked when the pathway opens for this patient (HRT-02: the estrogen and the progestogen). */
  ticked: boolean;
  start: string | null;
  range: string | null;
  routeFrequency: string | null;
  titration: string | null;
  label: string | null;
  rule: string | null;
  boundary: string | null;
  evidenceNote: string | null;
  status: string | null;
  pendingDecision: { row: number; topic: string; kept: string } | null;
  requiresReason: boolean;
  exceptionAboveMgWeek: number | null;
  noAutomaticDose: boolean;
  /** Not on a plan until MCOS approves the exact product. */
  heldBack: boolean;
  controlledSchedule: string | null;
  mcosStatus: string | null;
  compoundingStatus: string | null;
  billingUnit: string | null;
  inFormulary: boolean;
  price: {
    clinicPriceCents: number | null;
    pharmacyCostCents: number | null;
    active: boolean;
    oprPriceCents: number | null;
    oprLabel: string | null;
    oprConfidence: string | null;
  } | null;
}

export type ReadinessStatus = 'ok' | 'review' | 'caution' | 'locked' | 'missing';

export interface ReadinessRow {
  id: string;
  label: string;
  display: string | null;
  date: string | null;
  source: 'Lab' | 'Clinic' | 'Patient';
  status: ReadinessStatus;
  ruleIds: string[];
  fired: string[];
  required: boolean;
}

export interface HrtSelection {
  id: string;
  kind: 'selected' | 'no_pathway';
  pathwayId: string | null;
  pathwayName: string | null;
  medicationId: string | null;
  medicationName: string | null;
  /** Every product recorded with the choice, the main one first. */
  products: Array<{ medicationId: string; name: string }>;
  doseMgWeek: number | null;
  reason: string;
  band: HrtBand | null;
  rulesVersion: string;
  evaluationId: string | null;
  warnings: string[];
  clinician: { id: string; name: string } | null;
  createdAt: string;
}

export interface HrtAlert {
  id: string;
  ruleId: string;
  level: AlertLevel;
  text: string;
  checkinMonth: number | null;
  formResponseId: string | null;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: { id: string; name: string } | null;
  resolution: string | null;
  /** The evaluation resolving it can record (a clot alert: "Clot symptoms evaluated"), or null. */
  confirms: { key: string; label: string } | null;
}

export type MonitoringState = 'not_started' | 'upcoming' | 'due_now' | 'overdue' | 'done' | 'missed' | 'passed' | 'unscheduled';

export interface MonitoringRow {
  id: string;
  when: string;
  assess: string[];
  labs: string[];
  status: string | null;
  source: string | null;
  dueFrom: string | null;
  dueTo: string | null;
  state: MonitoringState;
  anchor: 'start' | 'dose_change' | null;
  repeats: 'every' | 'yearly' | 'individualized' | null;
  /** The hormone tests that show the check was done; empty for a visit. */
  tests: string[];
  onFile: Array<{ test: string; value: number; unit: string; drawnAt: string }>;
}

export interface HormoneLab {
  id: string;
  test: string;
  testLabel: string;
  value: number;
  unit: string;
  drawnAt: string;
  earlyMorning: boolean | null;
  fasting: boolean | null;
  flaggedLow: boolean | null;
  source: string;
  enteredBy: { id: string; name: string } | null;
  createdAt: string;
  voided: { at: string; by: { id: string; name: string } | null; reason: string } | null;
}

export interface VitalReading {
  id: string;
  systolic: number;
  diastolic: number;
  measuredAt: string;
  source: string;
  enteredBy: { id: string; name: string } | null;
  createdAt: string;
  voided: { at: string; by: { id: string; name: string } | null; reason: string } | null;
}

export interface HrtConfirmation {
  key: string;
  label: string;
  kind: 'boolean' | 'text' | 'rules' | 'date' | 'prior';
  unlocks: string[];
  /** The rules it lifts, by name. */
  unlockNames: string[];
  choices: string[] | null;
  value: boolean | string | string[] | null;
  note: string | null;
  setBy: { id: string; name: string } | null;
  setAt: string | null;
  /** Set before a newer report of the symptom: it no longer lifts the lock. */
  stale: { reportedAt: string } | null;
}

/** One Monthly check-in and the HRT answers on it, in the form's order. */
export interface HrtCheckinResponse {
  formResponseId: string;
  submittedAt: string;
  checkinMonth: number | null;
  answers: Array<{ ref: string; label: string; question: string; answer: string }>;
}

export interface HrtPatientView {
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    sexAtBirth: 'Male' | 'Female' | null;
    age: number | null;
    dob: string | null;
    heightIn: number | null;
    weightLb: number | null;
    weightSource: 'checkin' | 'intake' | null;
    bmi: number | null;
    state: string | null;
    goal: { primary: string | null; secondary: string | null; display: string | null };
  };
  membership: { member: boolean; reasons: string[]; track: HrtTrack | null; trackLabel: string | null };
  status: {
    programme: 'not_started' | 'started';
    programmeLabel: string;
    startedOn: string | null;
    startSource: 'confirmation' | 'plan' | null;
    usesTestosteroneNow: boolean;
    trackLabel: string | null;
    firstLabs: string | null;
    nextMonitoring: { id: string; when: string; dueFrom: string | null; dueTo: string | null; state: MonitoringState } | null;
    checkins: string;
    openAlerts: number;
    labAlertsOpen: number;
    selectedPathway: { id: string; name: string } | null;
  };
  summaryChips: string[];
  evaluation: {
    id: string;
    trigger: string;
    rulesVersion: string;
    createdAt: string;
    asOf: string;
    track: HrtTrack;
    exception: string | null;
    notice: string;
    pathways: HrtPathway[];
    phenotypes: HrtPhenotype[];
  } | null;
  pendingDecisions: Array<{ row: number; topic: string; kept: string }>;
  readiness: { rows: ReadinessRow[]; regulatory: Array<{ id: string; text: string; pathwayIds: string[] }> };
  products: Record<string, HrtProduct[]>;
  selections: HrtSelection[];
  alerts: { open: HrtAlert[]; resolved: HrtAlert[] };
  monitoring: {
    pathwayId: string;
    pathwayName: string;
    basis: 'selected' | 'top';
    startDate: string | null;
    rows: MonitoringRow[];
  } | null;
  labs: HormoneLab[];
  vitals: VitalReading[];
  confirmations: HrtConfirmation[];
  twin: {
    matchedPatients: number;
    minCohort: number;
    sufficient: false;
    label: string;
    summary: string;
    outcomes: Array<{ id: string; label: string; value: string }>;
    matchedOn: string;
    pathway: { id: string; name: string } | null;
  } | null;
  checkins: {
    count: number;
    last: { submittedAt: string; checkinMonth: number | null } | null;
    monthsOnHormones: number | null;
    /** When the next Monthly link goes out, or null when no subscription is active. */
    nextAt: string | null;
    nextSwitches: Record<string, string> | null;
    responses: HrtCheckinResponse[];
  };
  options: {
    tests: Array<{ test: string; label: string; units: string[] }>;
    specialistClearable: string[];
    priorResponses: string[];
  };
}

export interface ProgramPatientRow {
  patientId: string;
  firstName: string;
  lastName: string;
  goal: string | null;
  track: HrtTrack;
  trackLabel: string;
  reasons: string[];
  intakeAt: string | null;
  lastLabAt: string | null;
  lastLabEnteredAt: string | null;
  startedOn: string | null;
  selectedPathwayId: string | null;
  latestChoice: 'selected' | 'no_pathway' | null;
  /** A plan with a hormone product was signed and sent. */
  planReleased: boolean;
  waiting: boolean;
  readyLabel: 'Labs ready' | 'Intake done';
  readyAt: string | null;
  openAlerts: number;
  highestOpenLevel: AlertLevel | null;
}

/* ------------------------------------------------------------------ *
 * Queries
 * ------------------------------------------------------------------ */

export const hrtKey = (patientId: string) => ['hrt', patientId] as const;
export const HRT_PROGRAM_KEY = ['hrt-program'] as const;

/** The full clinic view for one patient. */
export function useHrtView(patientId: string, enabled = true) {
  return useQuery({
    queryKey: hrtKey(patientId),
    queryFn: () => api.get<HrtPatientView>(`/hrt/patients/${patientId}`),
    enabled,
  });
}

/** The clinic's HRT / TRT patients: which patient panel gets the hormone tabs, and the dashboard filter. */
export function useHrtProgram() {
  return useQuery({
    queryKey: HRT_PROGRAM_KEY,
    queryFn: () => api.get<{ patients: ProgramPatientRow[] }>('/hrt/program-patients'),
    staleTime: 30_000,
  });
}

/** The API's own words for a failed write: the field message for a 400, the engine's errors for a 422. */
export function errorText(error: unknown, fallback = 'Could not save. Try again.'): string {
  if (error instanceof ApiClientError) {
    if (Array.isArray(error.details)) {
      const first = (error.details as Array<{ message?: unknown }>).find((d) => typeof d?.message === 'string');
      if (first && typeof first.message === 'string') return first.message;
    }
    const details = error.details as { errors?: unknown } | undefined;
    if (details && Array.isArray(details.errors) && details.errors.length > 0) return details.errors.map(String).join(' ');
    return error.message;
  }
  return error instanceof Error ? error.message : fallback;
}

/* ------------------------------------------------------------------ *
 * Display helpers
 * ------------------------------------------------------------------ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sep 15", with the year only when it is not this year's. A plain YYYY-MM-DD is read as that calendar day. */
export function shortDay(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value.length === 10 ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '';
  const label = `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
  return date.getUTCFullYear() === new Date().getUTCFullYear() ? label : `${label}, ${date.getUTCFullYear()}`;
}

/** Today as YYYY-MM-DD in the clinician's own time zone, the latest date a draw or reading can have. */
export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Whole days since an ISO time, never negative. */
export function daysSince(value: string | null | undefined): number | null {
  if (!value) return null;
  const at = new Date(value).getTime();
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.floor((Date.now() - at) / 86_400_000));
}

/** The design's band word: "High", "Moderate", "Low", "Not relevant now", "Locked". */
export const BAND_WORD: Record<HrtBand, string> = {
  HIGH: 'High',
  MODERATE: 'Moderate',
  LOW: 'Low',
  SUPPRESS: 'Not relevant now',
  LOCKED: 'Locked',
};

/** The Treatment Relevance box beside each pathway: strongest band darkest, never a number. */
export const BAND_BOX: Record<HrtBand, string> = {
  HIGH: 'bg-forest text-white',
  MODERATE: 'bg-mint text-forest',
  LOW: 'bg-paper-deep text-navy',
  SUPPRESS: 'bg-paper-deep text-ink-muted',
  LOCKED: 'bg-paper-deep text-ink-muted',
};

/** Small band badge for tables and lists. */
export const BAND_BADGE: Record<HrtBand, string> = {
  HIGH: 'bg-forest text-white',
  MODERATE: 'bg-mint text-forest',
  LOW: 'bg-paper-deep text-navy',
  SUPPRESS: 'bg-paper-deep text-ink-muted',
  LOCKED: 'bg-paper-deep text-ink-muted',
};

/** Developer spec tab "Check-in Alerts": what each level asks of the clinic. */
export const LEVEL_TEXT: Record<AlertLevel, { word: string; when: string; badge: string; soft: string; line: string }> = {
  RED: { word: 'Red', when: 'Same-day clinical review', badge: 'bg-alert text-white', soft: 'bg-alert-soft', line: 'border-alert-line' },
  ORANGE: { word: 'Orange', when: 'Review before the next dose or refill', badge: 'bg-risk-orange text-white', soft: 'bg-risk-orange-soft', line: 'border-hairline' },
  YELLOW: { word: 'Yellow', when: 'Review at the next touchpoint', badge: 'bg-risk-yellow text-white', soft: 'bg-risk-yellow-soft', line: 'border-hairline' },
};

/** "Schedule III" from the formulary's DEA code ('3', '3N' ...). */
export function scheduleWord(code: string | null | undefined): string | null {
  if (!code) return null;
  const roman: Record<string, string> = { '1': 'I', '2': 'II', '3': 'III', '4': 'IV', '5': 'V' };
  const m = /^(\d)(N?)$/.exec(code.trim());
  return m ? `Schedule ${roman[m[1]!] ?? m[1]}` : null;
}

/** The controlled schedule of a pathway, from its products (testosterone rows carry '3'). */
export function pathwaySchedule(products: HrtProduct[] | undefined): string | null {
  const code = (products ?? []).map((p) => p.controlledSchedule).find(Boolean) ?? null;
  return scheduleWord(code);
}

/**
 * The note under a product, from the dose library: the pending Dr. Joe decision it is kept under, else its
 * prescribing rule or label fact, in the spec's own words.
 */
export function productNote(product: HrtProduct): string | null {
  if (product.pendingDecision) return `Dose range pending Dr. Joe (decision ${product.pendingDecision.row}): kept as written in v1.2.`;
  const text = product.rule ?? product.label ?? product.boundary;
  if (!text) return null;
  const sentence = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

/** The dose line when the pharmacy list has none: the dose library's start and range, or that there is no automatic dose. */
export function libraryDoseLine(product: HrtProduct): string {
  if (product.noAutomaticDose) return 'No automatic dose.';
  const freq = product.routeFrequency?.split(';')[0]?.trim().toLowerCase() ?? null;
  if (product.start && product.range) return `Start ${product.start}. Range ${product.range}${freq ? `, ${freq}` : ''}.`;
  if (product.start) return `${product.start.charAt(0).toUpperCase()}${product.start.slice(1)}.`;
  return 'Dose per the prescriber.';
}

/**
 * The products ticked when a pathway opens, as the API works them out for this patient: the dose library's default
 * or first main product, never an add-on, and the progestogen with an estrogen pathway when the uterus is present
 * (HRT-02 always: "the intact uterus makes progestogen protection part of the pathway").
 */
export function defaultProducts(products: HrtProduct[] | undefined): string[] {
  return (products ?? []).filter((p) => p.ticked).map((p) => p.medicationId);
}

/** "Estradiol transdermal patch and Micronized progesterone". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Route words per pathway (developer spec tab "Pathways", column Route, and tab "Route Fit", column Note). Shown in
 * the Clinical Pathway tab as plain words; the route's points never leave the API.
 */
export const ROUTE_WORDS: Record<string, { route: string; note: string | null }> = {
  'HRT-01': { route: 'Transdermal', note: 'Transdermal favored when clot risk is higher' },
  'HRT-02': { route: 'Transdermal or oral', note: null },
  'HRT-03': { route: 'Injection', note: 'Consensus does not recommend injections in women' },
  'HRT-04': { route: 'Transdermal (default)', note: 'Consensus-preferred route for HSDD' },
  'HRT-05': { route: 'Oral', note: 'Oral estrogen less preferred with clot risk, smoking or migraine with aura' },
  'HRT-06': { route: 'Compounded troche', note: 'Not evidence-preferred over a suitable FDA-approved product (ACOG 2023)' },
  'HRT-07': { route: 'Vaginal, local', note: null },
  'HRT-08': { route: 'Vaginal, exception', note: 'Selected / exception pathway' },
  'HRT-09': { route: 'Injection', note: null },
  'HRT-10': { route: 'Route neutral', note: null },
};

/** Lab and data rule per pathway (developer spec tab "Pathways", column Lab rule; tab "Lab Gates"). */
export const LAB_RULE: Record<string, 'male_t' | 'female_t' | 'systemic_estrogen' | 'local_vaginal'> = {
  'HRT-01': 'systemic_estrogen',
  'HRT-02': 'systemic_estrogen',
  'HRT-03': 'female_t',
  'HRT-04': 'female_t',
  'HRT-05': 'systemic_estrogen',
  'HRT-06': 'systemic_estrogen',
  'HRT-07': 'local_vaginal',
  'HRT-08': 'female_t',
  'HRT-09': 'male_t',
  'HRT-10': 'male_t',
};

/**
 * Short names for the symptom items (developer spec tab "Phenotypes", columns Item and Source). The answer beside
 * each is the patient's own, None to Very severe.
 */
export const SYMPTOM_NAME: Record<string, string> = {
  'SYM-FAND-LIBIDO': 'Low sexual desire',
  'SYM-FAND-ENERGY': 'Energy',
  'SYM-FAND-RECOVERY': 'Recovery',
  'SYM-FAND-SLEEP': 'Sleep quality',
  'SYM-FAND-WELL': 'Well-being or strength',
  'SYM-FVMS-HF': 'Hot flashes',
  'SYM-FVMS-NS': 'Night sweats',
  'SYM-FVMS-SLEEP': 'Sleep (not asked)',
  'SYM-FVMS-COG': 'Mental clarity or stress',
  'SYM-FGSM-DRY': 'Vaginal dryness or burning',
  'SYM-FGSM-DYS': 'Pain or discomfort with sex',
  'SYM-FGSM-GU': 'Urinary urgency, frequency or burning',
  'SYM-FGSM-IRR': 'Vaginal or vulvar irritation',
  'SYM-MH-LIB': 'Low libido',
  'SYM-MH-SEX': 'Sexual function',
  'SYM-MH-ENERGY': 'Energy',
  'SYM-MH-STRENGTH': 'Strength',
  'SYM-MH-MOOD': 'Mental clarity or stress',
  'SYM-MH-BODY': 'Weight and metabolism',
  'SYM-MH-REC': 'Recovery',
};

/**
 * Short names for the Level 1 rules a specialist clearance can unlock: the first clause of each rule's text in the
 * developer spec tab "Safety Rules".
 */
export const CLEARABLE_NAME: Record<string, string> = {
  'SAFE-002-BREAST': 'Breast cancer history',
  'SAFE-002-PROSTATE': 'Prostate cancer history',
  'SAFE-002-GYN': 'Uterine or ovarian cancer history',
  'SAFE-007': 'Pituitary pattern or concerning prolactin or neurologic findings',
  'SAFE-008': 'Liver disease',
  'SAFE-010': 'Past clot or clotting disorder',
  'SAFE-011': 'Past stroke, mini-stroke or heart attack',
  'SAFE-012': 'Heart attack or stroke in the past 6 months',
  'SAFE-013': 'Severe lower urinary tract symptoms',
};

/**
 * What each Monthly v7.3 hidden switch asks (developer spec tab "Monthly Switches", column Shows, in the form's own
 * words from data/typeform/monthly-checkin-v7.3.json).
 */
export const SWITCH_QUESTION: Record<string, string> = {
  hrt_ask_vms: 'Hot flashes or night sweats',
  hrt_ask_gsm: 'Vaginal or urinary symptoms',
  hrt_ask_bleeding: 'Bleeding',
  hrt_ask_breast: 'Breast symptoms',
  hrt_ask_virilization: 'Voice or clitoral change',
  hrt_ask_androgenic: 'Acne and hair changes',
  hrt_ask_desire: 'Sexual desire',
  hrt_ask_sexual_function: 'Sexual function',
  hrt_ask_fertility: 'Plans to have children',
  hrt_ask_side_effects: 'Breast tenderness or enlargement',
  hrt_ask_osa: 'Sleep apnea symptoms',
  hrt_ask_luts: 'Urinary symptoms',
  hrt_ask_hypoxia: 'Lung disease or living at altitude',
  hrt_ask_iron: 'Iron overload',
};

/** Evidence tiers (developer spec tab "Evidence"). */
export const TIER_WORD: Record<string, string> = {
  A: 'Regulatory or label',
  B: 'Guideline or consensus',
  C: 'Trial or study',
};

/** The words for a monitoring row's state. */
export const MONITORING_STATE: Record<MonitoringState, { word: string; tone: string }> = {
  not_started: { word: 'After start', tone: 'bg-paper-deep text-ink-muted' },
  upcoming: { word: 'Upcoming', tone: 'bg-info-soft text-navy' },
  due_now: { word: 'Due now', tone: 'bg-risk-yellow-soft text-risk-yellow' },
  overdue: { word: 'Overdue', tone: 'bg-risk-orange-soft text-risk-orange' },
  done: { word: 'Labs on file', tone: 'bg-mint text-forest' },
  missed: { word: 'Not on file', tone: 'bg-paper-deep text-ink-muted' },
  passed: { word: 'Window passed', tone: 'bg-paper-deep text-ink-muted' },
  unscheduled: { word: 'Individualized', tone: 'bg-paper-deep text-ink-muted' },
};

const TEST_SHORT: Record<string, string> = { total_t: 'Total T', hct: 'Hct', shbg: 'SHBG', lh: 'LH', fsh: 'FSH' };

/** "Hct 51%, Total T 640 ng/dL (Sep 30)", from the draws that count for a monitoring check. */
export function onFileLine(onFile: MonitoringRow['onFile']): string | null {
  if (onFile.length === 0) return null;
  const parts = onFile.map((d) => `${TEST_SHORT[d.test] ?? d.test} ${d.unit === '%' ? `${d.value}%` : `${d.value} ${d.unit}`}`);
  const days = [...new Set(onFile.map((d) => shortDay(d.drawnAt)))];
  return `${parts.join(', ')} (${days.join(' and ')})`;
}

/** "Aug 12 to Aug 26", "Jan 1, 2027", or null before the start date is known. */
export function dueWindow(row: Pick<MonitoringRow, 'dueFrom' | 'dueTo'>): string | null {
  if (!row.dueFrom) return null;
  if (!row.dueTo || row.dueTo === row.dueFrom) return shortDay(row.dueFrom);
  return `${shortDay(row.dueFrom)} to ${shortDay(row.dueTo)}`;
}

/** A pathway's prior response, from the newest `prior:HRT-xx` confirmation. */
export function priorWord(confirmations: HrtConfirmation[], pathwayId: string): string {
  const row = confirmations.find((c) => c.key === `prior:${pathwayId}`);
  const value = typeof row?.value === 'string' ? row.value : null;
  if (!value || value === 'none') return 'None recorded';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * A lock's "what unlocks it", in words (from the API), and the confirmation that records it when this patient's
 * confirmations list has it: the screen links straight to that row.
 */
export function unlockWords(lock: HrtFinding, confirmations: HrtConfirmation[]): { text: string; key: string | null } | null {
  if (!lock.unlockText) return null;
  return { text: lock.unlockText, key: lock.unlockKey && confirmations.some((c) => c.key === lock.unlockKey) ? lock.unlockKey : null };
}

/** The finding behind a rule id on any pathway, for a readiness line that fired it. */
export function findingFor(view: HrtPatientView, ruleId: string): HrtFinding | null {
  for (const p of view.evaluation?.pathways ?? []) {
    const hit = [...p.locks, ...p.review, ...p.caution].find((f) => f.id === ruleId);
    if (hit) return hit;
  }
  return null;
}

/** The hint under a rule: its id and the sources the spec gives for it. */
export function ruleSource(finding: Pick<HrtFinding, 'id' | 'cite'> & { source?: string | null }): string {
  return [`Rule ${finding.id}`, finding.cite, finding.source].filter(Boolean).join(' · ');
}

/** Whether this signed-in role may record a clinical decision (requireClinician on the API). */
export const CLINICIAN_ROLES = ['ADMIN', 'CLINICIAN'] as const;
